/**
 * OnboardingTour — "⚡ New Deal in 60 seconds" first-run walkthrough.
 *
 * Shows an 8-step playbook overlay the FIRST time a user lands on /v3
 * (per-user tracked via `localStorage.tour_completed_<userId>`). Each
 * step links to a real page in the app, so it doubles as a guided
 * tour of the pipeline plumbing.
 *
 * Design choices:
 *  - Full-screen modal (not a floating spotlight) — keeps every step
 *    self-contained and immune to layout shifts.
 *  - "Skip for now" persists dismissal; "Show me later" leaves it
 *    incomplete so it reappears next login.
 *  - "Start the deal" CTA on each step deep-links to the relevant page.
 *  - Restartable via the RestartTourButton (used on Beta Cockpit / help
 *    menu so an operator can rerun anytime).
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Rocket, Search, Save, Sparkles, FileSignature, Landmark,
  Zap, Trophy, ChevronRight, ChevronLeft, X as XIcon, Play,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";


// ── The 8-step playbook ────────────────────────────────────────────────
export const TOUR_STEPS = [
  {
    key:  "discover",
    icon: Search,
    title: "Discover a property",
    tagline: "10 seconds",
    body: "Type any US address into Discover. RentCast returns live MLS + AVM + comps in one shot.",
    cta:  { label: "Open Discover", to: "/v3/crm/discover" },
  },
  {
    key:  "analyze",
    icon: Sparkles,
    title: "Get the AI verdict",
    tagline: "5 seconds",
    body: "AI Deal Score returns GO / MAYBE / PASS with MAO, ARV, and margin math from the Golden Metrics API — the single source of truth.",
    cta:  { label: "Try Deal Analyzer", to: "/v3/tools/analyzer" },
  },
  {
    key:  "save",
    icon: Save,
    title: "Save to Pipeline",
    tagline: "3 seconds",
    body: "One click drops the property into your CRM at the Lead stage. Every intel tab (Comps, Repairs, BRRRR, Sub-To…) is now live on the Deal Card.",
    cta:  { label: "See your Pipeline", to: "/v3/crm/pipeline" },
  },
  {
    key:  "call",
    icon: Rocket,
    title: "Call the seller — guided",
    tagline: "10 minutes",
    body: "Open the Deal Card and click Call Mode. Live Deal Analyzer surfaces MAO + Critical Systems check while you're on the phone.",
    cta:  { label: "How Call Mode works", to: "/v3/crm/pipeline" },
  },
  {
    key:  "offer",
    icon: FileSignature,
    title: "Send the Purchase Agreement",
    tagline: "1 minute",
    body: "Generate a strategy-specific PA (Cash / Sub-2 / Novation / Seller Finance / Wholetail) and fire it through DocuSign. Signed contracts auto-flow to the Transaction Board.",
    cta:  { label: "Open Contracts", to: "/v3/crm/contracts" },
  },
  {
    key:  "tc",
    icon: Landmark,
    title: "Track EMD + POF",
    tagline: "30 seconds",
    body: "Every signed contract lands in the Transaction Board. Tap the EMD or POF chip on a card to mark it received — the lane auto-advances the moment escrow clears.",
    cta:  { label: "Transaction Board", to: "/v3/dispo/transactions" },
  },
  {
    key:  "blast",
    icon: Zap,
    title: "Match & blast to buyers",
    tagline: "20 seconds",
    body: "Hot-stage deals show a Match count. One tap fires a personalized SMS + email to every matching buyer in your network.",
    cta:  { label: "Match & Blast", to: "/v3/crm/pipeline?ready=1" },
  },
  {
    key:  "close",
    icon: Trophy,
    title: "Close and log the win",
    tagline: "day of close",
    body: "Assign the deal, log the assignment fee, and publish the win to your Wins page. Your KPI dashboard automatically snapshots the payout.",
    cta:  { label: "Closings tracker", to: "/v3/crm/closings" },
  },
];


// ── LocalStorage helpers (per-user completion state) ───────────────────
function completionKey(userId) {
  return `tour_completed_${userId || "anon"}`;
}
function skipKey(userId) {
  return `tour_skipped_${userId || "anon"}`;
}

export function hasCompletedTour(userId) {
  try {
    return !!window.localStorage.getItem(completionKey(userId))
        || !!window.localStorage.getItem(skipKey(userId));
  } catch {
    return true; // fail closed so we never spam users
  }
}

export function markTourCompleted(userId) {
  try {
    window.localStorage.setItem(completionKey(userId), new Date().toISOString());
    window.localStorage.removeItem(skipKey(userId));
  } catch {
    /* noop — private-window fallback: tour just reappears next session */
  }
}

export function markTourSkipped(userId) {
  try {
    window.localStorage.setItem(skipKey(userId), new Date().toISOString());
  } catch { /* noop */ }
}

export function resetTour(userId) {
  try {
    window.localStorage.removeItem(completionKey(userId));
    window.localStorage.removeItem(skipKey(userId));
  } catch { /* noop */ }
}


// ── The overlay ────────────────────────────────────────────────────────
export default function OnboardingTour() {
  const { user } = useAuth() || {};
  const [open, setOpen] = useState(false);
  const [idx, setIdx]   = useState(0);
  const navigate = useNavigate();
  const uid = user?.id;

  // Auto-open on first load if never completed / skipped.
  useEffect(() => {
    if (!uid) return;
    if (!hasCompletedTour(uid)) {
      // Small delay so the dashboard has time to render underneath.
      const t = setTimeout(() => setOpen(true), 600);
      return () => clearTimeout(t);
    }
  }, [uid]);

  // Global event to allow "Restart tour" buttons anywhere in the app.
  useEffect(() => {
    const handler = () => { setIdx(0); setOpen(true); };
    window.addEventListener("tour:start", handler);
    return () => window.removeEventListener("tour:start", handler);
  }, []);

  const step = useMemo(() => TOUR_STEPS[idx], [idx]);

  if (!open || !step) return null;

  const isLast = idx === TOUR_STEPS.length - 1;
  const Icon = step.icon;

  const dismiss = () => { markTourSkipped(uid); setOpen(false); };
  const complete = () => { markTourCompleted(uid); setOpen(false); };
  const next = () => { if (isLast) complete(); else setIdx((i) => i + 1); };
  const back = () => setIdx((i) => Math.max(0, i - 1));

  const jumpTo = () => {
    if (!step.cta?.to) return;
    markTourCompleted(uid);
    setOpen(false);
    navigate(step.cta.to);
  };

  return (
    <div
      className="fixed inset-0 z-[9999] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="onboarding-tour-overlay"
      onClick={dismiss}
    >
      <div
        className="w-full max-w-lg rounded-2xl bg-white shadow-2xl overflow-hidden relative"
        onClick={(e) => e.stopPropagation()}
        data-testid="onboarding-tour-card"
      >
        {/* Header */}
        <div
          className="px-6 py-5 text-white relative"
          style={{ backgroundImage: "linear-gradient(135deg, #4F46E5, #4338CA)" }}
        >
          <button
            type="button"
            onClick={dismiss}
            className="absolute top-3 right-3 text-white/70 hover:text-white p-1"
            data-testid="onboarding-tour-close"
            aria-label="Close tour"
          >
            <XIcon className="w-4 h-4" />
          </button>
          <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] font-black bg-white/15 px-2 py-1 rounded-full">
            <Rocket className="w-3 h-3" /> New Deal in 60 Seconds
          </div>
          <div className="mt-3 flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-white/15 grid place-items-center shrink-0">
              <Icon className="w-5 h-5" />
            </div>
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-widest font-bold text-white/70">
                Step {idx + 1} of {TOUR_STEPS.length} · {step.tagline}
              </div>
              <div className="text-xl font-black leading-tight truncate">{step.title}</div>
            </div>
          </div>
          {/* Progress dots */}
          <div className="mt-4 flex gap-1">
            {TOUR_STEPS.map((s, i) => (
              <button
                key={s.key}
                type="button"
                onClick={() => setIdx(i)}
                data-testid={`onboarding-tour-dot-${i}`}
                className={`h-1.5 rounded-full transition-all ${
                  i === idx ? "bg-amber-300 w-8" : i < idx ? "bg-white/80 w-5" : "bg-white/25 w-5"
                }`}
                aria-label={`Jump to step ${i + 1}`}
              />
            ))}
          </div>
        </div>

        {/* Body */}
        <div className="p-6" data-testid="onboarding-tour-body">
          <p className="text-slate-700 text-[14px] leading-relaxed">{step.body}</p>

          {step.cta?.to && (
            <button
              type="button"
              onClick={jumpTo}
              data-testid="onboarding-tour-jump"
              className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-black text-white transition-all"
              style={{ backgroundImage: "linear-gradient(135deg, #F59E0B, #D97706)" }}
            >
              <Play className="w-4 h-4" />
              Take me to “{step.cta.label}”
            </button>
          )}
        </div>

        {/* Footer nav */}
        <div className="px-6 pb-5 flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={dismiss}
            className="text-[12px] text-slate-500 hover:text-slate-700 font-semibold"
            data-testid="onboarding-tour-skip"
          >
            Skip for now
          </button>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={back}
              disabled={idx === 0}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-slate-200 text-[12.5px] font-bold text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="onboarding-tour-back"
            >
              <ChevronLeft className="w-3.5 h-3.5" />
              Back
            </button>
            <button
              type="button"
              onClick={next}
              className="inline-flex items-center gap-1 px-4 py-1.5 rounded-lg text-white text-[12.5px] font-bold shadow-sm"
              style={{ backgroundImage: "linear-gradient(135deg, #6366F1, #4F46E5)" }}
              data-testid="onboarding-tour-next"
            >
              {isLast ? "Finish" : "Next"}
              {!isLast && <ChevronRight className="w-3.5 h-3.5" />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


// ── Small helper: a button/link that restarts the tour from anywhere.
export function RestartTourButton({ className = "", children }) {
  const { user } = useAuth() || {};
  return (
    <button
      type="button"
      onClick={() => {
        resetTour(user?.id);
        window.dispatchEvent(new Event("tour:start"));
      }}
      data-testid="onboarding-tour-restart-btn"
      className={className || "inline-flex items-center gap-1.5 text-[12px] font-bold text-indigo-700 hover:text-indigo-800"}
    >
      <Rocket className="w-3.5 h-3.5" />
      {children || "Restart the New Deal tour"}
    </button>
  );
}
